import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod/v4';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import type { DriverAttributes, UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { BUILT_IN_ROLE_KEY, ROLE_SCOPE } from '@klareospace/pharos-lib-types';
import { buildScopesFromIds, getScopeIdsByLevel } from '@klareospace/pharos-lib-utils';
import { getApiErrorDetail } from '@klareospace/pharos-lib-front-utils';
import { buildOptionalPhoneNumberSchema } from '@klareospace/pharos-lib-front-utils/phone-validation';

import type { InviteUserParams } from '../api';
import type { UpdateUserData } from '../api/user.api';
import type { UserFormInput } from '../schemas/user-form.schema';
import { useCreateInvitation } from './use-create-invitation';
import { useUpdateUser } from './use-update-user';

// ─── Types ────────────────────────────────────────────────────────────────────

export type { UserFormInput };

export interface UseUserFormOptions {
  initialCarrierId?: string;
  initialRoles?: string[];
  initialSiteId?: string;
  initialSpaceId?: string;
  /**
   * Whether the drawer is currently open. UserDrawer is permanently mounted by its callers (no
   * conditional render, no key), so this is the only signal available to re-seed create-mode
   * defaults from the CURRENT options on every closed→open transition, and to re-sync while
   * open+pristine when a locked option resolves asynchronously after mount (e.g. a carrier's
   * space, fetched separately from the drawer).
   */
  open?: boolean;
}

export interface UseUserFormReturn {
  form: UseFormReturn<UserFormInput>;
  isPending: boolean;
  onSubmit: (data: UserFormInput, spaceSiteMap: Map<string, string[]>) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getCreateFormDefaults(options?: UseUserFormOptions): UserFormInput {
  return {
    carrierId: options?.initialCarrierId ?? null,
    defaultVehicleId: null,
    email: '',
    firstName: '',
    lastName: '',
    phone: null,
    roles: options?.initialRoles ?? [],
    siteIds: options?.initialSiteId ? [options.initialSiteId] : [],
    spaceIds: options?.initialSpaceId ? [options.initialSpaceId] : [],
  };
}

function getEditFormDefaults(user: UserWithResolvedScopes): UserFormInput {
  return {
    carrierId: user.driverAttributes?.carrierId ?? null,
    defaultVehicleId: user.driverAttributes?.defaultVehicleId ?? null,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone ?? null,
    roles: user.roles ?? [],
    siteIds: getScopeIdsByLevel(user.scopes, ROLE_SCOPE.SITE),
    spaceIds: getScopeIdsByLevel(user.scopes, ROLE_SCOPE.SPACE),
  };
}

function getFormDefaults(user: UserWithResolvedScopes | null, options?: UseUserFormOptions): UserFormInput {
  return user ? getEditFormDefaults(user) : getCreateFormDefaults(options);
}

function buildSpacesWithSites(spaceSiteMap: Map<string, string[]>): Set<string> {
  return new Set(
    Array.from(spaceSiteMap.entries())
      .filter(([, siteIds]) => siteIds.length > 0)
      .map(([spaceId]) => spaceId),
  );
}

function buildEffectiveSpaceIds(data: UserFormInput, spaceSiteMap: Map<string, string[]>): string[] {
  const spacesWithSites = buildSpacesWithSites(spaceSiteMap);
  return (data.spaceIds ?? []).filter((id) => !spacesWithSites.has(id));
}

/** Included whenever the form's roles include the driver role — never gated on carrierId presence
 * (the schema's superRefine already guarantees it when the driver role is selected). */
function buildDriverAttributes(data: UserFormInput): DriverAttributes | undefined {
  if (!data.roles.includes(BUILT_IN_ROLE_KEY.DRIVER)) return undefined;
  return { carrierId: data.carrierId ?? '', defaultVehicleId: data.defaultVehicleId ?? null };
}

function buildUpdatePayload(
  user: UserWithResolvedScopes,
  data: UserFormInput,
  spaceSiteMap: Map<string, string[]>,
): UpdateUserData {
  const nonSpaceSiteScopes = (user.scopes ?? []).filter(
    (s) => s.level !== ROLE_SCOPE.SPACE && s.level !== ROLE_SCOPE.SITE,
  );
  const effectiveSpaceIds = buildEffectiveSpaceIds(data, spaceSiteMap);
  const scopes = [...nonSpaceSiteScopes, ...buildScopesFromIds(effectiveSpaceIds, data.siteIds ?? [])];
  const driverAttributes = buildDriverAttributes(data);

  return {
    firstName: data.firstName,
    lastName: data.lastName,
    phone: data.phone ?? null,
    roles: data.roles,
    scopes,
    ...(driverAttributes ? { driverAttributes } : {}),
  };
}

function buildCreateParams(data: UserFormInput, spaceSiteMap: Map<string, string[]>): InviteUserParams {
  const effectiveSpaceIds = buildEffectiveSpaceIds(data, spaceSiteMap);
  const scopes = buildScopesFromIds(effectiveSpaceIds, data.siteIds ?? []);
  const driverAttributes = buildDriverAttributes(data);

  return {
    email: data.email ?? '',
    firstName: data.firstName,
    lastName: data.lastName,
    roles: data.roles,
    scopes,
    ...(data.phone ? { phone: data.phone } : {}),
    ...(driverAttributes ? { driverAttributes } : {}),
  };
}

interface CarrierRequiredCandidate {
  carrierId?: string | null;
  roles: string[];
}

function validateCarrierRequiredForDriver(data: CarrierRequiredCandidate, ctx: z.RefinementCtx, message: string): void {
  const isDriver = data.roles.includes(BUILT_IN_ROLE_KEY.DRIVER);
  if (isDriver && !data.carrierId?.trim()) {
    ctx.addIssue({ code: 'custom', message, path: ['carrierId'] });
  }
}

// Fields identical in shape between the create and edit schemas — only the messages differ.
// Extended (never duplicated) by each mode's schema below for the genuinely differing fields
// (email is create-only, phone rules differ, siteIds/spaceIds optionality differs).
interface SharedFieldMessages {
  firstNameRequired: string;
  lastNameRequired: string;
  roleRequired: string;
}

function buildSharedSchema(messages: SharedFieldMessages): z.ZodObject<{
  carrierId: z.ZodOptional<z.ZodNullable<z.ZodString>>;
  defaultVehicleId: z.ZodOptional<z.ZodNullable<z.ZodString>>;
  firstName: z.ZodString;
  lastName: z.ZodString;
  roles: z.ZodArray<z.ZodString>;
}> {
  return z.object({
    carrierId: z.string().nullable().optional(),
    defaultVehicleId: z.string().nullable().optional(),
    firstName: z.string().trim().min(1, messages.firstNameRequired),
    lastName: z.string().trim().min(1, messages.lastNameRequired),
    roles: z.array(z.string()).min(1, messages.roleRequired),
  });
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUserForm(
  user: UserWithResolvedScopes | null,
  onSuccess: () => void,
  options?: UseUserFormOptions,
): UseUserFormReturn {
  const { t } = useTranslation('settings');
  const isEditMode = user !== null;
  const createInvitation = useCreateInvitation();
  const updateUser = useUpdateUser();

  const schema = useMemo(() => {
    if (isEditMode) {
      const carrierRequiredMessage = t('users.edit.validation.carrierRequired');
      return buildSharedSchema({
        firstNameRequired: t('users.edit.validation.firstNameRequired'),
        lastNameRequired: t('users.edit.validation.lastNameRequired'),
        roleRequired: t('users.edit.validation.rolesRequired'),
      })
        .extend({
          phone: buildOptionalPhoneNumberSchema({ invalid: t('users.edit.validation.phoneInvalid') }).transform(
            (val) => (val && val.number.trim() ? val : null),
          ),
          siteIds: z.array(z.string()),
          spaceIds: z.array(z.string()),
        })
        .superRefine((data, ctx) => validateCarrierRequiredForDriver(data, ctx, carrierRequiredMessage));
    }

    const carrierRequiredMessage = t('users.inviteDrawer.validation.carrierRequired');
    return buildSharedSchema({
      firstNameRequired: t('users.inviteDrawer.validation.firstNameRequired'),
      lastNameRequired: t('users.inviteDrawer.validation.lastNameRequired'),
      roleRequired: t('users.inviteDrawer.validation.roleRequired'),
    })
      .extend({
        email: z.string().trim().email(t('users.inviteDrawer.validation.emailInvalid')),
        phone: buildOptionalPhoneNumberSchema({ invalid: t('users.inviteDrawer.validation.phoneInvalid') }),
        siteIds: z.array(z.string()).optional(),
        spaceIds: z.array(z.string()).optional(),
      })
      .superRefine((data, ctx) => validateCarrierRequiredForDriver(data, ctx, carrierRequiredMessage));
  }, [isEditMode, t]);

  const form = useForm<UserFormInput>({
    defaultValues: getFormDefaults(user, options),
    mode: 'onChange',
    resolver: zodResolver(schema) as Resolver<UserFormInput>,
  });

  // Reseed on user id change (edit target switch, or null<->user transition).
  useEffect(() => {
    form.reset(getFormDefaults(user, options));
    // Reset only when the user id changes (or goes null→user / user→null)
  }, [user?.id]);

  // UserDrawer is permanently mounted (no conditional render, no key) by every caller, so the
  // effect above never re-fires for create mode (user stays null across opens/closes). Re-seed
  // create-mode defaults from the CURRENT options on every closed→open transition, and re-sync
  // while open+pristine when a locked option resolves asynchronously (e.g. a carrier-derived
  // space id fetched after the drawer has already mounted). Never touches a dirty form mid-edit.
  const wasOpenRef = useRef(false);
  const initialRolesKey = options?.initialRoles?.join(',') ?? '';

  useEffect(() => {
    const isOpen = options?.open ?? false;
    if (!isEditMode) {
      // `formState.isDirty` is a lazily-tracked react-hook-form proxy value that does not reflect
      // recent changes when read from an effect without a prior render-time read — dirtyFields
      // does not have that caveat, so it is the reliable "is the form pristine" check here.
      const isPristine = Object.keys(form.formState.dirtyFields).length === 0;
      const justOpened = isOpen && !wasOpenRef.current;
      const resyncWhileOpenAndPristine = isOpen && wasOpenRef.current && isPristine;
      if (justOpened || resyncWhileOpenAndPristine) {
        form.reset(getCreateFormDefaults(options));
      }
    }
    wasOpenRef.current = isOpen;
  }, [
    isEditMode,
    options?.open,
    options?.initialCarrierId,
    initialRolesKey,
    options?.initialSiteId,
    options?.initialSpaceId,
    form,
  ]);

  function onSubmit(data: UserFormInput, spaceSiteMap: Map<string, string[]>): void {
    if (isEditMode) {
      if (updateUser.isPending || !user) return;

      const payload = buildUpdatePayload(user, data, spaceSiteMap);
      void updateUser.mutateAsync({ data: payload, id: user.id }).then(() => onSuccess());
      return;
    }

    // Create mode
    if (createInvitation.isPending) return;

    const params = buildCreateParams(data, spaceSiteMap);

    createInvitation.mutateAsync(params).then(
      () => {
        toast.success(t('users.inviteDrawer.success'));
        onSuccess();
      },
      (error: unknown) => {
        const detail = getApiErrorDetail(error);
        toast.error(detail.message || t('users.inviteDrawer.error'));
      },
    );
  }

  return {
    form,
    isPending: createInvitation.isPending || updateUser.isPending,
    onSubmit,
  };
}
